/**
 * Work Orders API Layer.
 *
 * React Query hooks for work order CRUD, labor/parts/materials, and completion.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";
import type {
  LaborEntryCreate,
  MaterialEntryCreate,
  PartEntryCreate,
  WorkOrder,
  WorkOrderCreate,
} from "@/types/work-order";

interface UseWOsParams {
  page?: number;
  pageSize?: number;
  status?: string;
  assetId?: string;
  priority?: string;
}

export function useWorkOrders(params: UseWOsParams = {}) {
  const { page = 1, pageSize = 25, status, assetId, priority } = params;
  return useQuery<PaginatedResponse<WorkOrder>>({
    queryKey: ["work-orders", { page, pageSize, status, assetId, priority }],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("page", String(page));
      sp.set("page_size", String(pageSize));
      if (status) sp.set("status", status);
      if (assetId) sp.set("asset_id", assetId);
      if (priority) sp.set("priority", priority);
      const resp = await apiClient.get(`/api/v1/work-orders?${sp}`);
      return resp.data as PaginatedResponse<WorkOrder>;
    },
  });
}

export function useWorkOrder(woId: string | undefined) {
  return useQuery<WorkOrder>({
    queryKey: ["work-orders", woId],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/work-orders/${woId}`);
      return resp.data as WorkOrder;
    },
    enabled: !!woId,
  });
}

export function useCreateWorkOrder() {
  const qc = useQueryClient();
  return useMutation<WorkOrder, Error, WorkOrderCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post("/api/v1/work-orders", data);
      return resp.data as WorkOrder;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-orders"] }),
  });
}

export function useUpdateWorkOrder(woId: string) {
  const qc = useQueryClient();
  return useMutation<WorkOrder, Error, Record<string, unknown>>({
    mutationFn: async (data) => {
      const resp = await apiClient.put(`/api/v1/work-orders/${woId}`, data);
      return resp.data as WorkOrder;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-orders"] }),
  });
}

export function useAddLabor(woId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, LaborEntryCreate>({
    mutationFn: async (data) =>
      (await apiClient.post(`/api/v1/work-orders/${woId}/labor`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-orders", woId] }),
  });
}

export function useAddPart(woId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, PartEntryCreate>({
    mutationFn: async (data) =>
      (await apiClient.post(`/api/v1/work-orders/${woId}/parts`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-orders", woId] }),
  });
}

export function useAddMaterial(woId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, MaterialEntryCreate>({
    mutationFn: async (data) =>
      (await apiClient.post(`/api/v1/work-orders/${woId}/materials`, data))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-orders", woId] }),
  });
}

export function useCompleteWorkOrder(woId: string) {
  const qc = useQueryClient();
  return useMutation<WorkOrder, Error, void>({
    mutationFn: async () => {
      const resp = await apiClient.post(`/api/v1/work-orders/${woId}/complete`);
      return resp.data as WorkOrder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-orders"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}
